import {
  type JSX,
  useCallback,
  memo,
  type MouseEvent as ReactMouseEvent,
  type ComponentProps,
} from "react";

import {
  CartLineQuantityAdjustButton,
  useCart,
  useCartLine,
} from "@shopify/hydrogen-react";
import {
  type CartLine,
  type CartLineUpdateInput,
} from "@shopify/hydrogen-react/storefront-api-types";

import { useLegacyCart } from "@cart/LegacyCartContext";
import { getRelatedCartLinesToRemove } from "@tablescape/utilities";

type CartGiftLineQuantityAdjustButtonProps = ComponentProps<
  typeof CartLineQuantityAdjustButton
> & {
  onQuantityChange?: (changeset: {
    adjust: ComponentProps<typeof CartLineQuantityAdjustButton>["adjust"];
    quantity: number;
  }) => void;
};

/**
 * The `<CartGiftLineQuantityAdjustButton />` component renders a button that adjusts the cart line's quantity when pressed.
 *
 * It must be a descendent of `<CartLineProvider/>` and `<CartProvider/>`.
 */
const CartGiftLineQuantityAdjustButtonBase = (
  props: CartGiftLineQuantityAdjustButtonProps
): JSX.Element => {
  const { linesRemove, linesUpdate } = useCart();
  const { updateToLegacyCart } = useLegacyCart();
  const {
    attributes: cartLineAttributes,
    id: cartLineId,
    merchandise: { id: merchandiseId } = {},
    quantity: cartLineQuantity = 0,
    selectedGiftsWithPurchase,
  } = useCartLine();
  const { adjust, children, onClick, onQuantityChange, ...passthroughProps } =
    props;

  const handleAdjust = useCallback(
    (event?: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
      event?.preventDefault();

      if (cartLineId && adjust === "remove") {
        const linesToRemove = getRelatedCartLinesToRemove(
          cartLineId,
          selectedGiftsWithPurchase
        );

        linesRemove(linesToRemove);
        updateToLegacyCart({
          updates: linesToRemove.map((id) => ({
            id,
            merchandiseId,
            quantity: 0,
          })),
        });

        onClick?.(event);
        onQuantityChange?.({ adjust, quantity: 0 });
        return;
      }

      const quantity =
        adjust === "decrease" ? cartLineQuantity - 1 : cartLineQuantity + 1;

      if (cartLineId && quantity <= 0) {
        const linesToRemove = getRelatedCartLinesToRemove(
          cartLineId,
          selectedGiftsWithPurchase
        );

        linesRemove(linesToRemove);
        updateToLegacyCart({
          updates: linesToRemove.map((id) => ({
            id,
            merchandiseId,
            quantity: 0,
          })),
        });

        onClick?.(event);
        onQuantityChange?.({ adjust, quantity });
        return;
      }

      const lineUpdate = {
        id: cartLineId ?? "",
        quantity,
        attributes: (cartLineAttributes ??
          []) as CartLineUpdateInput["attributes"],
      } satisfies CartLineUpdateInput;

      const linesToUpdate = [lineUpdate];

      if (adjust === "decrease" && cartLineId) {
        const removableGiftLines =
          Array.isArray(selectedGiftsWithPurchase) &&
          selectedGiftsWithPurchase.length > quantity
            ? selectedGiftsWithPurchase.slice(
                quantity - selectedGiftsWithPurchase.length
              )
            : undefined;

        if (removableGiftLines?.length) {
          const giftLinesToRemove = removableGiftLines
            .filter((line): line is CartLine => !!line)
            .map(
              (giftLine) =>
                ({
                  id: giftLine.id,
                  quantity: 0,
                  attributes: (giftLine.attributes ??
                    []) as CartLineUpdateInput["attributes"],
                } satisfies CartLineUpdateInput)
            );

          linesToUpdate.push(...giftLinesToRemove);
        }
      }

      linesUpdate(linesToUpdate);
      updateToLegacyCart({
        updates: linesToUpdate.map(({ id, quantity }) => ({
          id,
          merchandiseId,
          quantity,
        })),
      });
      onClick?.(event);
      onQuantityChange?.({ adjust, quantity });
    },
    [
      adjust,
      cartLineAttributes,
      cartLineId,
      cartLineQuantity,
      linesRemove,
      linesUpdate,
      merchandiseId,
      onClick,
      onQuantityChange,
      selectedGiftsWithPurchase,
      updateToLegacyCart,
    ]
  );

  return (
    <CartLineQuantityAdjustButton
      adjust={adjust}
      onClick={handleAdjust}
      {...passthroughProps}
    >
      {children}
    </CartLineQuantityAdjustButton>
  );
};

export const CartGiftLineQuantityAdjustButton = memo(
  CartGiftLineQuantityAdjustButtonBase
);
